// TimeLedger — deterministic logic.
// Pure functions. No network, no LocalStorage access here — App.tsx owns
// persistence so this stays trivially testable.

export interface Project {
  id: string
  name: string
  client: string
  quotedPrice: number       // USD
  estimatedHours: number    // initial estimate
  status: 'active' | 'done' | 'archived'
  createdAt: string         // ISO date
}

export interface TimeEntry {
  id: string
  projectId: string
  date: string              // ISO date (YYYY-MM-DD)
  hours: number             // decimal hours, e.g. 1.5
  description: string
}

export interface ProjectStats {
  projectId: string
  name: string
  client: string
  status: Project['status']
  quotedPrice: number
  estimatedHours: number
  actualHours: number
  effectiveHourlyRate: number   // quotedPrice / actualHours (0 if no hours)
  estimatedHourlyRate: number   // quotedPrice / estimatedHours
  hoursVariance: number         // actual - estimated
  hoursVariancePct: number      // (actual - estimated) / estimated * 100
  health: 'green' | 'yellow' | 'red'
}

export interface PortfolioStats {
  totalQuoted: number
  totalEarned: number           // quoted price of completed (status === 'done') projects
  totalActualHours: number
  totalEstimatedHours: number
  blendedHourlyRate: number
  projectCount: number
  onTrackCount: number
  overBudgetCount: number
}

export function statsForProject(project: Project, entries: TimeEntry[]): ProjectStats {
  const own = entries.filter((e) => e.projectId === project.id)
  const actualHours = own.reduce((sum, e) => sum + e.hours, 0)
  const effective = actualHours > 0 ? project.quotedPrice / actualHours : 0
  const estimated = project.estimatedHours > 0 ? project.quotedPrice / project.estimatedHours : 0
  const variance = actualHours - project.estimatedHours
  const variancePct = project.estimatedHours > 0 ? (variance / project.estimatedHours) * 100 : 0

  let health: ProjectStats['health'] = 'green'
  if (project.estimatedHours > 0) {
    const ratio = actualHours / project.estimatedHours
    if (ratio > 1.1) health = 'red'
    else if (ratio > 0.8) health = 'yellow'
  }

  return {
    projectId: project.id,
    name: project.name,
    client: project.client,
    status: project.status,
    quotedPrice: project.quotedPrice,
    estimatedHours: project.estimatedHours,
    actualHours: Number(actualHours.toFixed(2)),
    effectiveHourlyRate: Number(effective.toFixed(2)),
    estimatedHourlyRate: Number(estimated.toFixed(2)),
    hoursVariance: Number(variance.toFixed(2)),
    hoursVariancePct: Number(variancePct.toFixed(1)),
    health,
  }
}

export function portfolioStats(projects: Project[], entries: TimeEntry[]): PortfolioStats {
  const liveProjects = projects.filter((p) => p.status !== 'archived')
  let totalQuoted = 0
  let totalEarned = 0
  let totalActual = 0
  let totalEstimated = 0
  let onTrack = 0
  let over = 0

  for (const p of liveProjects) {
    const s = statsForProject(p, entries)
    totalQuoted += s.quotedPrice
    if (p.status === 'done') totalEarned += s.quotedPrice
    totalActual += s.actualHours
    totalEstimated += s.estimatedHours
    if (s.health === 'red') over++
    else onTrack++
  }

  const blendedNumerator = totalEarned > 0 ? totalEarned : totalQuoted
  return {
    totalQuoted,
    totalEarned,
    totalActualHours: Number(totalActual.toFixed(2)),
    totalEstimatedHours: Number(totalEstimated.toFixed(2)),
    blendedHourlyRate: totalActual > 0 ? Number((blendedNumerator / totalActual).toFixed(2)) : 0,
    projectCount: liveProjects.length,
    onTrackCount: onTrack,
    overBudgetCount: over,
  }
}

function csvEscape(value: string | number): string {
  const s = String(value ?? '')
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

export function entriesToCsv(projects: Project[], entries: TimeEntry[]): string {
  const projectById = new Map(projects.map((p) => [p.id, p]))
  const header = 'Date,Project,Client,Hours,Description,Quoted Price,Effective Rate (running)\n'
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date))
  const hoursSoFar = new Map<string, number>()
  const rows = sorted.map((e) => {
    const p = projectById.get(e.projectId)
    const cumulative = (hoursSoFar.get(e.projectId) ?? 0) + e.hours
    hoursSoFar.set(e.projectId, cumulative)
    const runningRate = p && cumulative > 0 ? (p.quotedPrice / cumulative).toFixed(2) : ''
    return [
      e.date,
      csvEscape(p?.name ?? ''),
      csvEscape(p?.client ?? ''),
      e.hours.toString(),
      csvEscape(e.description),
      p?.quotedPrice.toString() ?? '',
      runningRate,
    ].join(',')
  })
  return header + rows.join('\n') + '\n'
}

export interface QuoteCheck {
  proposedPrice: number
  proposedHours: number
  proposedRate: number
  historicalAvgRate: number
  recommendation: 'underpriced' | 'reasonable' | 'overpriced'
  delta: number
  message: string
}

export function checkQuote(
  proposedPrice: number,
  proposedHours: number,
  projects: Project[],
  entries: TimeEntry[]
): QuoteCheck {
  const stats = portfolioStats(projects, entries)
  const avg = stats.blendedHourlyRate
  const proposedRate = proposedHours > 0 ? proposedPrice / proposedHours : 0
  const delta = proposedRate - avg
  let recommendation: QuoteCheck['recommendation'] = 'reasonable'
  let message = ''

  if (avg === 0) {
    message = 'Not enough history yet — log time on a few completed projects first.'
  } else if (proposedRate < avg * 0.85) {
    recommendation = 'underpriced'
    message = `Proposed $${proposedRate.toFixed(0)}/hr is ${Math.abs(((proposedRate - avg) / avg) * 100).toFixed(0)}% below your blended $${avg.toFixed(0)}/hr. Consider raising the price or trimming scope.`
  } else if (proposedRate > avg * 1.2) {
    recommendation = 'overpriced'
    message = `Proposed $${proposedRate.toFixed(0)}/hr is ${(((proposedRate - avg) / avg) * 100).toFixed(0)}% above your blended $${avg.toFixed(0)}/hr. Strong margin — make sure the client sees the value.`
  } else {
    message = `Proposed $${proposedRate.toFixed(0)}/hr is in line with your blended $${avg.toFixed(0)}/hr.`
  }

  return {
    proposedPrice,
    proposedHours,
    proposedRate: Number(proposedRate.toFixed(2)),
    historicalAvgRate: Number(avg.toFixed(2)),
    recommendation,
    delta: Number(delta.toFixed(2)),
    message,
  }
}
